import {
  Box,
  Checkbox,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import ArrButton from '../../common/arr-button/ArrButton';
import {t} from 'i18next';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import ArrIconButton from '../../common/arr-icon-button/ArrIconButton';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../common/arr-menu/ArrMenu';
import {ReactComponent as AddFieldSortIcon} from '../../../assets/images/add-fields-sort.svg';
import {ReactComponent as CheckedIcon} from '../../../../src/assets/images/icon-checked.svg';
import {ReactComponent as UnCheckedIcon} from '../../../../src/assets/images/icon-unchecked.svg';
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import {
  ASSIGNMENT_ACTION_MENU,
  assignmentsColumns,
  createDocumentViewerListRequestPayloadFromAssignments,
} from '../AssignmentsUtils';
import './AssignmentsDataGrid.scss';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import {ReactComponent as NoDataIcon} from '../../../assets/images/no-data-found.svg';
import ArrRadio from '../../common/arr-radio/ArrRadio';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {ArrowDropUp} from '@mui/icons-material';
import {
  useAssignmentList,
  useCallBulkActionsLikePopupNonbg,
  useCallBulkActionsPopupNonBg,
  useCompleteUncompleteAllDocs,
  useCopyAssignment,
  useDeleteAssignmentGroup,
  useDoActionNonBg,
} from '../../../hooks/assignments/useAssignments';
import {
  IAssignmentResponse,
  IBulkActionLikePopupRequest,
} from '../../../api/types/assignment.type';
import {useNavigate} from 'react-router-dom';
import {isEmpty, isNil} from 'lodash';
import {useDispatch, useSelector} from 'react-redux';
import {
  clearBackSourceTabIndex,
  setAssignmentList,
  setSelectedData,
} from '../../../redux/slice/assignmentSlice';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {SourceTypeCode, ToastType} from '../../../shared/constants';
import ArrModal from '../../common/arr-modal/ArrModal';
import {RootState} from '../../../redux/store';
import {
  useDocumentList,
  useDocumentViewerListId,
} from '../../../hooks/document/useDocument';
import {
  DocumentListSourceInfo,
  DocumentViewerListIdRequest,
} from '../../../api/types/document.type';

import ArrDocMaxLimitWarning from '../../common/arr-doc-max-limit-warning/ArrDocMaxLimitWarning';
import {
  clearDocViewState,
  createDocumentListRequestPayload,
} from '../../document-viewer/docViewerUtils';
import {
  storeDocumentListMasterId,
  storeSourceId,
  storeSourceTypeCode,
} from '../../../redux/slice/docViewSlice';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../redux/slice/docListSlice';
import BulkFolderUnFolder from '../../action-menu/bulk-folder-unfolder/BulkFolderUnFolder';
import {ActionSelection} from '../../doc-explorer/ViewDocExplorer';
import ActionLikeDocuments, {
  IActionListItem,
} from '../../action-menu/action-like-documents/ActionLikeDocuments';
import ArrLoadingSpinner from '../../common/arr-loading-spinner/ArrLoadingSpinner';
import BulkTagUntag from '../../action-menu/bulk-tag-untag/BulkTagUntag';
import BulkTaggingWarning from '../../action-menu/bulk-tagging-warning/BulkTaggingWarning';

const ColumnMenuItem: React.FC<CustomMenuItem> = React.memo(
  ({id, label, checked, disabled, onToggle}) => {
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (disabled) return;
      onToggle?.();
    };

    return (
      <MenuItem
        dense
        disableRipple
        disabled={disabled}
        onClick={handleClick}
        role="menuitemcheckbox"
        aria-checked={checked}
        aria-disabled={disabled}
        data-testid={`assignments-data-grid-menu-item-${id}`}
      >
        <ListItemIcon>
          <Checkbox
            data-testid={`assignments-grid-column-menu-checkbox-${id}`}
            edge="start"
            checked={checked}
            tabIndex={-1}
            disableRipple
            onClick={(e) => {
              e.stopPropagation();
              onToggle?.();
            }}
            size="small"
            icon={<UnCheckedIcon />}
            checkedIcon={<CheckedIcon />}
            disabled={disabled}
          />
        </ListItemIcon>
        <ListItemText primary={label} />
      </MenuItem>
    );
  }
);

interface AssignmentsDataGridProps {
  assignmentGroupId: string | null;
}
const NoRowsOverlay = () => (
  <Box className="no-project-data-found">
    <NoDataIcon />
    <Typography
      className="typo-text headline3_size no-assignment-text"
      data-testid={`assignments-data-grid-message-no-data`}
    >
      {t('dashboard.reviewer.emptyData')}
    </Typography>
  </Box>
);

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const initialActionList = [
  {
    id: 'family_members',
    label: t(`common.actionMenu.familyMembers`),
    isLoading: false,
    count: 0,
    selected: false,
  },
  {
    id: 'email_docs',
    label: t(`common.actionMenu.emailThread`),
    isLoading: false,
    count: 0,
    selected: false,
  },
  {
    id: 'near_duplicates',
    label: t(`common.actionMenu.nearDuplicates`),
    isLoading: false,
    count: 0,
    selected: false,
  },
];

const ROW_HEIGHT = 36;

const AssignmentsDataGrid: React.FC<AssignmentsDataGridProps> = ({
  assignmentGroupId,
}) => {
  const menuRef2 = useRef<ArrMenuHandle | null>(null);
  const [rows, setRows] = useState<IAssignmentResponse[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'assignmentId',
      sort: 'asc',
    },
  ]);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const actionMenuRef = useRef<ArrMenuHandle>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    type: ToastType[];
  }>({open: false, message: '', type: [ToastType.Warning]});
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalDescription, setModalDescription] = useState<string>('');
  const [selectedActionMenu, setSelectedActionMenu] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);
  const [showDocLimitDialog, setShowDocLimitDialog] = useState<boolean>(false);
  const [docLimitSourceTypeCode, setDocLimitSourceTypeCode] =
    useState<SourceTypeCode>();
  const [isBulkFolderAction, setIsBulkFolderAction] = useState<boolean>(false);
  const [masterDocListId, setMasterDocListId] = useState<number>(0);
  const [selectedNewExistingFolder, setSelectedNewExistingFolder] = useState<
    ActionSelection | null | undefined
  >();
  const [bulkSelectedAction, setBulkSelectedAction] = useState<string>('');
  const [isActionLikeModal, setIsActionLikeModal] = useState<boolean>(false);
  const [actionLikeChangeList, setActionLikeChangeList] =
    useState<IActionListItem[]>();
  const [analyticsIndicatorStatus, setAnalyticsIndicatorStatus] =
    useState<number>(0);
  const [nonBgStandardCount, setNonBgStandardCount] = useState<number>(0);
  const [isBulkTagAction, setIsBulkTagAction] = useState(false);
  const [isBulkWarning, setIsBulkWarning] = useState(false);
  const [selectedNewExistingTag, setSelectedNewExistingTag] =
    useState<ActionSelection | null>(null);
  const [nonBgTotalDocCount, setNonBgTotalDocCount] = useState<number>(0);
  const [newTagId, setNewTagId] = useState<number | undefined>(0);
  const [newFolderId, setNewFolderId] = useState<number | undefined>(0);

  const selectedMenuItem = useRef<CustomMenuItem | undefined>(undefined);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    mutate: mutateDeleteAssignment,
    error: errorDeleteAssignment,
    isSuccess: isSuccessDeleteAssignment,
  } = useDeleteAssignmentGroup();

  const {
    mutate: mutateCompleteUncompleteAllDocs,
    isPending: completeUncompleteLoading,
  } = useCompleteUncompleteAllDocs();

  const {mutate: copyAssignment} = useCopyAssignment();

  const {mutate: fetchDocumentViewerListId, isPending: isDocViewLoading} =
    useDocumentViewerListId();

  const {mutate: fetchDocumentList, isPending: isDocListLoading} =
    useDocumentList();

  const {
    mutate: callBulkActionsPopupNonBg,
    isPending: isLoadingBulkActionsPopup,
  } = useCallBulkActionsPopupNonBg();
  const {
    mutate: callBulkActionsLikePopupNonbg,
    isPending: isLoadingBulkActionsLikePopup,
  } = useCallBulkActionsLikePopupNonbg();
  const {mutate: doActionNonBg, isPending: isDoActionLoading} =
    useDoActionNonBg(
      () => {
        // Reset states
        resetFolderActionStates();

        refetchAssignmentList();
        setToastMessage(
          t('docExplorer.actionMenu.bulkTaggingWarning.doActionSuccess')
        );
        setToastStatus(ToastType.Success);
        setShowToast(true);
      },
      (_error) => {
        setToastMessage(
          t('docExplorer.actionMenu.bulkTaggingWarning.doActionError')
        );
        setToastStatus(ToastType.Error);
        setShowToast(true);
      }
    );

  useEffect(() => {
    if (errorDeleteAssignment) {
      setToast({
        open: true,
        message: t(`assignment.delete.errorDeleteAssignment`),
        type: [ToastType.Error],
      });
    } else if (isSuccessDeleteAssignment) {
      setToast({
        open: true,
        message: t(`assignment.delete.successAssignment`),
        type: [ToastType.Success],
      });
      setShowModal(false);
      setSelectedRowId(null);
      dispatch(
        setSelectedData({
          type: 'assignment',
          mode: 'delete',
          assignmentId: 0,
          assignmentGroupId: -1,
        })
      );
      refetchAssignmentList();
    }
  }, [isSuccessDeleteAssignment, errorDeleteAssignment]);

  const baseColumns = useMemo<GridColDef[]>(() => {
    return assignmentsColumns().map((col) => ({
      ...col,
      minWidth: col.minWidth ?? 174,
    }));
  }, []);

  const [visibility, setVisibility] = useState<GridColumnVisibilityModel>(() =>
    baseColumns.reduce((acc, c) => {
      const hiddenFields = [
        'createdByUser',
        'createdOn',
        'isAllowReviwerRemarks',
        'isAllowPopoutPanels',
      ];

      return {
        ...acc,
        [c.field]: !hiddenFields.includes(c.field),
      };
    }, {} as GridColumnVisibilityModel)
  );

  const handleManageColumnsClick = (e: React.MouseEvent<HTMLElement>) => {
    menuRef2.current?.open(e.currentTarget as HTMLElement);
  };

  type ColumnDef = ReturnType<typeof assignmentsColumns>[number];
  type FieldKey = ColumnDef['field'];

  const visibleCount = useMemo(
    () => Object.values(visibility).filter(Boolean).length,
    [visibility]
  );

  const onPageChangeHandler = (value: number) => {
    setPageNumber(value);
  };

  const renderActionsCell = useCallback(
    (params: GridRenderCellParams) => {
      const row = params.row as IAssignmentResponse;

      const handleRadioChange = (val: string) => {
        setSelectedRowId(Number(val));
      };

      return (
        <Box
          className="assignment-grid-action"
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              setSelectedRowId(row.assignmentId);
            }
          }}
        >
          <ArrRadio
            dataTestId={`assignment-radio-${row.assignmentId}`}
            id={`assignment-gid-radio-${row.assignmentId}`}
            name={`assignment-radio-${row.assignmentId}`}
            value={selectedRowId ? selectedRowId.toString() : ''}
            onChange={handleRadioChange}
            data={[
              {
                value: row.assignmentId.toString(),
                label: '',
              },
            ]}
            size="xs"
            className="row-radio"
          />
        </Box>
      );
    },
    [selectedRowId]
  );

  // Find the currently selected assignment row
  const selectedAssignment = useMemo(
    () => rows.find((r) => r.assignmentId === selectedRowId) ?? null,
    [rows, selectedRowId]
  );

  const documentViewerListPayload = useCallback(
    (
      totalDocumentToView: number,
      objSourceOverrides?: Partial<DocumentListSourceInfo>
    ): DocumentViewerListIdRequest =>
      createDocumentViewerListRequestPayloadFromAssignments({
        sourceID: selectedAssignment?.assignmentId ?? 0,
        selectedAssignmentName: selectedAssignment?.assignmentName ?? '',
        totalDocumentToView,
        objSourceOverrides: objSourceOverrides ?? {},
      }),
    [selectedAssignment?.assignmentId, selectedAssignment?.assignmentName]
  );

  const documentListPayload = useCallback(
    (totalDocumentToView: number) =>
      createDocumentListRequestPayload({
        sourceId: selectedAssignment?.assignmentId ?? 0,
        sourceTypeCode: SourceTypeCode.ASSIGNMENT,
        docViewSourceTypeCode: SourceTypeCode.ASSIGNMENT,
        docViewSessionOverrides: {
          assignmentSortDetails: [
            {
              sourceID: selectedAssignment?.assignmentId ?? 0,
              columnManual: '',
              columnOptimized: '',
              columnIDsOptimized: '',
              columnOrderOptimized: '',
              columnIDsManual: '',
              columnOrderManual: '',
              showCompletedOptimized: true,
              showCompletedManual: false,
              configuredShowCompletedDoc: false,
              columnDisplayManual: '',
              columnDataTypeOptimized: [],
              columnDataTypeManual: [],
            },
          ],
        },
        sourceInfoOverrides: {
          totalDocumentToView,
          destinationContextTypeCode: 'DOCLIST',
        },
      }),
    [selectedAssignment?.assignmentId]
  );

  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );

  const hasSelection = !!selectedAssignment;
  const totalDocs = selectedAssignment?.totalDocuments ?? 0;
  const hasDocs = totalDocs > 0;
  const hasLeftToDoDocs = (selectedAssignment?.toBeReviewedDocuments ?? 0) > 0;
  const hasCompletedDocs = (selectedAssignment?.completeCount ?? 0) > 0;

  const actionMenuItems = useMemo(() => {
    return ASSIGNMENT_ACTION_MENU.map((item) => {
      // Handle the "view" parent item and its submenu items
      if (item.id === 'view' && Array.isArray(item.submenu)) {
        const submenu = item.submenu.map((subItem) => {
          // All "view" submenu items require: selection AND hasDocs
          return {
            ...subItem,
            disabled: !(hasSelection && hasDocs),
          };
        });

        return {
          ...item,
          disabled: false,
          submenu,
        };
      }

      let disabled = false;
      switch (item.id) {
        // Enabled if an assignment is selected
        case 'edit_Assignment':
        case 'copy_Assignment':
        case 'delete_Assignment':
          disabled = !hasSelection;
          break;

        // Enabled only if selected AND assignment has 1+ docs
        case 'tag_All_Documents':
        case 'folder_All_Documents':
        case 'export_Metadata':
          disabled = !(hasSelection && hasDocs);
          break;

        // Enabled only if selected AND has docs AND there are 1+ docs left to do
        case 'complete_All_Documents':
          disabled = !(hasSelection && hasDocs && hasLeftToDoDocs);
          break;

        // Enabled only if selected AND has docs AND there are 1+ completed docs
        case 'uncomplete_All_Documents':
          disabled = !(hasSelection && hasDocs && hasCompletedDocs);
          break;

        // Always enabled
        case 'new_Assignment':
        default:
          disabled = false;
      }

      return {...item, disabled};
    });
  }, [hasSelection, hasDocs, hasLeftToDoDocs, hasCompletedDocs]);

  const updatedColumns = useMemo(
    () => [
      {
        field: '',
        headerName: '',
        width: 48,
        sortable: false,
        filterable: false,
        headerClassName: 'typo-text label2_size sticky-col left-col-header',
        cellClassName: 'action-cell sticky-col left-col-cell',
        renderCell: renderActionsCell,
      },
      ...baseColumns,
    ],
    [baseColumns, renderActionsCell]
  );

  const sortField = sortModel[0]?.field || 'assignmentId';
  const sortDirection = sortModel[0]?.sort || 'asc';

  const shouldFetch = !isEmpty(assignmentGroupId) && !isNil(assignmentGroupId);
  const queryString = shouldFetch
    ? `?DisplayLength=${itemsPerPage}&Pagenumber=${pageNumber}&Direction=${sortDirection}&SortingColumnName=${sortField}&AssignmentGroupId=${assignmentGroupId}`
    : '';
  const {
    data: assignmentList,
    isLoading: isAssignmentListLoading,
    refetch: refetchAssignmentList,
  } = useAssignmentList(queryString, shouldFetch);

  useEffect(() => {
    setRows(assignmentList?.tableData ?? []);
    dispatch(setAssignmentList(assignmentList));
  }, [assignmentList]);

  const toggle = useCallback(
    (field: FieldKey) => {
      setVisibility((prev) => {
        const currentlyVisible = Boolean(prev[field]);
        const currentlyVisibleCount =
          Object.values(prev).filter(Boolean).length;

        if (currentlyVisible && currentlyVisibleCount === 1) {
          return prev;
        }

        return {...prev, [field]: !currentlyVisible};
      });
    },
    [setVisibility]
  );

  const items = useMemo(
    () =>
      baseColumns.map((c) => {
        const checked = Boolean(visibility[c.field]);
        const disabled = checked && visibleCount === 1;
        return {
          id: c.field,
          label: c.headerName || '',
          checked,
          disabled,
          onToggle: () => toggle(c.field),
        };
      }),
    [baseColumns, visibility, visibleCount, toggle]
  );

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model);
  }, []);

  const handleColumnVisibilityModelChange = (
    newModel: GridColumnVisibilityModel
  ) => {
    const visible = Object.values(newModel ?? {}).filter(Boolean).length;
    if (visible === 0) {
      return;
    }
    setVisibility(newModel);
  };

  const getIncludeFlags = (
    actionLikeChangeList: IActionListItem[] | undefined
  ): {
    includeNearDuplicates: boolean;
    includeFamilyMember: boolean;
    includeEmailThreads: boolean;
  } => {
    if (!actionLikeChangeList) {
      return {
        includeNearDuplicates: false,
        includeFamilyMember: false,
        includeEmailThreads: false,
      };
    }

    const nearDuplicates = actionLikeChangeList.find(
      (item) => item.id === 'near_duplicates'
    );
    const familyMembers = actionLikeChangeList.find(
      (item) => item.id === 'family_members'
    );
    const emailDocs = actionLikeChangeList.find(
      (item) => item.id === 'email_docs'
    );

    return {
      includeNearDuplicates: nearDuplicates?.selected === true,
      includeFamilyMember: familyMembers?.selected === true,
      includeEmailThreads: emailDocs?.selected === true,
    };
  };

  const callBulkActionsLikePopupApiCall = (
    getActionType: string,
    bulkActionTreeIDs: string[] | number[],
    docCount: number
  ) => {
    let extraPayload = {};

    if (getActionType === 'TAG') {
      extraPayload = {
        selectedHitsDetails: [
          {
            sourceID: 0,
            pureHits: true,
            emailThread: true,
            nearDup: true,
            family: true,
            conceptualSimilar: true,
            uniquePureHits: true,
            uniqueFamilyMembers: true,
            s: 0,
            p: true,
            e: true,
            n: true,
            f: true,
            c: true,
            up: true,
            uf: true,
            ingestionExceptionDetails: [
              {
                ieid: 0,
                errorType: 'string',
              },
            ],
          },
        ],
        ...extraPayload,
      };
    }

    const likePopupPayload: IBulkActionLikePopupRequest = {
      sourceType: SourceTypeCode.ASSIGNMENT,
      actionType: getActionType,
      elementID: 'divBulkAction',
      sourceTypeContext: '',
      sourceDetails: JSON.stringify({
        LstBulkActionContext: [{ID: selectedAssignment?.assignmentId}],
        ContextContainerID: selectedAssignment?.assignmentId,
      }),
      destinationTypeContext: '',
      selectedHitsDetails: [],
      documentCount: '0',
      masterDocListID: 0,
      isAdd: true,
      documentListMasterID: 0,
      docAssign: docCount,
      selectedDetinationIds: JSON.stringify({
        BulkActionTreeIDs: bulkActionTreeIDs,
      }),
      ...extraPayload,
    };

    callBulkActionsLikePopupNonbg(likePopupPayload, {
      onSuccess: (response) => {
        setIsActionLikeModal(true);
        setAnalyticsIndicatorStatus(response.analyticsIndicatorStatus ?? 0);
      },
      onError: (error: any) => {
        triggerToast(
          error?.message ||
            t('docExplorer.actionMenu.bulkTaggingWarning.bulkTagError'),
          ToastType.Error
        );
      },
    });
  };

  const doActionApiCall = (
    bulkActionTreeIDs: string[],
    selectedAction: string,
    docCount?: number
  ) => {
    const isAdd = selectedAction === 'folderDocs';

    const includeFlags = getIncludeFlags(actionLikeChangeList);

    const doActionNonBgPayload = {
      sourceType: SourceTypeCode.ASSIGNMENT,
      actionType: 'FOLDER',
      elementId: 'divBulkAction',
      selectedList: {
        lstBulkActionContext: [{ID: selectedAssignment?.assignmentId}],
        contextContainerID: selectedAssignment?.assignmentId,
      },
      destinationList: {
        bulkActionTreeIDs: newFolderId
          ? [newFolderId.toString()]
          : bulkActionTreeIDs,

        isAdd: isAdd,
        sampleMethod: 'STANDARD',
        numberToAssign: docCount,
        ...includeFlags,
      },
    };

    doActionNonBg(doActionNonBgPayload);
  };

  const handleActionMenuItemClick = (item: CustomMenuItem) => {
    if (item?.disabled) {
      return;
    }

    selectedMenuItem.current = {...item};

    if (item.id == 'new_Assignment') {
      dispatch(
        setSelectedData({
          type: 'assignment',
          mode: 'create',
          assignmentId: 0,
          assignmentGroupId: assignmentGroupId ? Number(assignmentGroupId) : -1,
        })
      );
      navigate('/Assignment/Create');
    } else if (item.id == 'edit_Assignment') {
      dispatch(clearBackSourceTabIndex());
      dispatch(
        setSelectedData({
          type: 'assignment',
          mode: 'edit',
          assignmentId: selectedRowId ?? 0,
          assignmentGroupId: assignmentGroupId ? Number(assignmentGroupId) : -1,
        })
      );
      navigate('/Assignment/Edit');
    } else if (
      item.id === 'complete_All_Documents' ||
      item.id === 'uncomplete_All_Documents'
    ) {
      if (loginUserData?.baseRoleId !== loginUserData?.roleId) {
        return triggerToast(
          t('assignment.manageReviewers.impersonatedError'),
          ToastType.Error
        );
      }
      setShowModal(true);

      setModalDescription(
        t(
          `assignment.manageReviewers.modalTitle.${item.id === 'complete_All_Documents' ? 'completeAllDocs' : 'uncompleteAllDocs'}`
        )
      );
      setSelectedActionMenu(item.id);
    } else if (item.id == 'delete_Assignment') {
      setShowModal(true);
      setSelectedActionMenu(item.id);
      setModalDescription(t('assignment.delete.deleteThisAssignment'));
    } else if (item.id == 'copy_Assignment') {
      setShowModal(true);
      setSelectedActionMenu(item.id);
      setModalDescription(t('assignment.copy.modalDescription'));
    } else if (
      item.id === 'view_All_Docs_in_DocView' ||
      item.id === 'view_in_DocView_in_new_tab'
    ) {
      menuRef2.current?.close();
      setDocLimitSourceTypeCode(SourceTypeCode.DOCVIEW);
      setShowDocLimitDialog(true);
    } else if (item.id === 'view_All_Docs_in_DocList') {
      menuRef2.current?.close();
      setDocLimitSourceTypeCode(SourceTypeCode.DOCLIST);
      setShowDocLimitDialog(true);
    } else if (item.id === 'folder_All_Documents') {
      const commonPayload = {
        sourceType: SourceTypeCode.ASSIGNMENT,
        actionType: 'FOLDERNonBG',
        elementID: 'divBulkAction',
        selectedHitsDetails: [],
        sourceDetails: JSON.stringify({
          LstBulkActionContext: [{ID: selectedAssignment?.assignmentId}],
          ContextContainerID: selectedAssignment?.assignmentId,
        }),
      };

      callBulkActionsPopupNonBg(commonPayload, {
        onSuccess: (response) => {
          setNonBgStandardCount(response.documentCounts.standardCount);
          setIsBulkFolderAction(true);
        },
      });
    } else if (item.id === 'tag_All_Documents') {
      if (!selectedRowId || !selectedAssignment?.assignmentId) return;
      const payload = {
        sourceType: SourceTypeCode.ASSIGNMENT,
        actionType: 'TAGNonBG',
        elementID: 'divBulkAction',
        selectedHitsDetails: [],
        sourceDetails: JSON.stringify({
          LstBulkActionContext: [{ID: selectedAssignment.assignmentId}],
          ContextContainerID: selectedAssignment.assignmentId,
        }),
      };

      callBulkActionsPopupNonBg(payload, {
        onSuccess: (response) => {
          setNonBgStandardCount(response.documentCounts.standardCount);
          setIsBulkTagAction(true);
        },
      });
    } else if (item.id === 'export_Metadata') {
      if (!selectedAssignment?.assignmentId) return;
      menuRef2.current?.close();
      navigate('/Report/ExportData', {
        state: {
          item: {
            navigationSourceType: SourceTypeCode.ASSIGNMENT,
            source: [selectedAssignment.assignmentId.toString()],
            sourceName: selectedAssignment.assignmentName ?? '',
            sourceDisable: true,
          },
        },
      });
    }
  };

  const onContinueDocLimit = useCallback(
    (totalDocumentToView: number) => {
      setShowDocLimitDialog(false);
      switch (docLimitSourceTypeCode) {
        case SourceTypeCode.DOCVIEW: {
          fetchDocumentViewerListId(
            documentViewerListPayload(totalDocumentToView),
            {
              onSuccess: (response) => {
                clearDocViewState();
                const masterId =
                  response?.documentListID ?? response?.defaultMasterID ?? 0;

                switch (selectedMenuItem.current?.id) {
                  case 'view_All_Docs_in_DocView': {
                    dispatch(storeDocumentListMasterId(String(masterId)));
                    dispatch(storeSourceTypeCode(SourceTypeCode.ASSIGNMENT));
                    dispatch(
                      storeSourceId(selectedAssignment?.assignmentId ?? 0)
                    );

                    navigate('/DocumentViewer/DocView');
                    break;
                  }
                  case 'view_in_DocView_in_new_tab': {
                    const newWindow = window.open('about:blank', '_blank');
                    if (newWindow) {
                      dispatch(storeDocumentListMasterId(String(masterId)));
                      dispatch(storeSourceTypeCode(SourceTypeCode.ASSIGNMENT));
                      dispatch(
                        storeSourceId(selectedAssignment?.assignmentId ?? 0)
                      );

                      newWindow.location.href = `${window.location.origin}/DocumentViewer/DocView`;
                    }
                    break;
                  }
                  default: {
                    break;
                  }
                }
              },
              onError: (error) => {
                triggerToast(error?.message ?? '', ToastType.Error);
              },
            }
          );
          break;
        }
        case SourceTypeCode.DOCLIST: {
          fetchDocumentList(documentListPayload(totalDocumentToView), {
            onSuccess: (response) => {
              const masterIdRaw =
                response.docId ??
                response.doclistId ??
                response.documentListID ??
                response.defaultMasterID ??
                response.docViewSessionObjectsRes?.documentListMasterID ??
                0;
              const masterId = Number(masterIdRaw) || 0;
              clearDocViewState();
              setTimeout(() => {
                dispatch(
                  setDocListBackNavigation({
                    label: t('assignment.assignments'),
                    path: `${window.location.pathname}${window.location.search}`,
                  })
                );
                dispatch(setDocListId(Number(masterId)));
                dispatch(storeSourceTypeCode(SourceTypeCode.ASSIGNMENT));
                dispatch(storeSourceId(selectedAssignment?.assignmentId ?? 0));
                navigate('/Document/DocList');
              }, 3000);
            },
            onError: (error) => {
              triggerToast(error?.message ?? '', ToastType.Error);
            },
          });
          break;
        }
        default: {
          break;
        }
      }
    },
    [
      dispatch,
      docLimitSourceTypeCode,
      documentListPayload,
      documentViewerListPayload,
      fetchDocumentList,
      fetchDocumentViewerListId,
      navigate,
      selectedAssignment?.assignmentId,
    ]
  );

  const onCancelDocLimit = useCallback(() => {
    setShowDocLimitDialog(false);
  }, []);

  const handlePrimaryButton = () => {
    if (
      selectedActionMenu === 'complete_All_Documents' ||
      selectedActionMenu === 'uncomplete_All_Documents'
    ) {
      handleCompleteUnCompleteAllDocs();
    } else if (selectedActionMenu === 'delete_Assignment') {
      mutateDeleteAssignment({
        selectedGroupId: Number(selectedRowId),
        isAssignmentGroup: false,
      });
    } else if (selectedActionMenu === 'copy_Assignment') {
      copyAssignment(
        {
          assignmentId: Number(selectedRowId),
        },
        {
          onSuccess: () => {
            setToast({
              open: true,
              message: t(`assignment.copy.successCopyingAssignment`),
              type: [ToastType.Success],
            });
            setShowModal(false);
            setSelectedRowId(null);
            refetchAssignmentList();
          },
          onError: () => {
            setToast({
              open: true,
              message: t(`assignment.copy.errorCopyingAssignment`),
              type: [ToastType.Error],
            });
          },
        }
      );
    }
  };

  const checkLoading = () => {
    if (
      selectedActionMenu === 'complete_All_Documents' ||
      selectedActionMenu === 'uncomplete_All_Documents'
    ) {
      return completeUncompleteLoading;
    }
    return false; // Add other conditions for different actions if needed
  };

  const handleCompleteUnCompleteAllDocs = () => {
    const payload = {
      assignmentId: Number(selectedRowId),
      completealldocuments: selectedActionMenu === 'complete_All_Documents',
    };
    mutateCompleteUncompleteAllDocs(payload, {
      onSuccess: (data) => {
        triggerToast(data.message, ToastType.Success);
        setShowModal(false);
        refetchAssignmentList();
      },
      onError: (error) => {
        triggerToast(error.message, ToastType.Error);
      },
    });
  };

  const triggerToast = (message: string, status: ToastType) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const handleSecondaryButton = () => {
    setShowModal(false);
  };

  const handleBulkFolderContinue = async (
    selectedAction: string,
    docCount?: number,
    newFolderId?: number | undefined
  ) => {
    setBulkSelectedAction(selectedAction);
    setNewFolderId(newFolderId);

    const bulkActionTreeIDs = (() => {
      if (Array.isArray(selectedNewExistingFolder?.ids))
        return selectedNewExistingFolder.ids;
      if (typeof selectedNewExistingFolder?.ids === 'string')
        return [selectedNewExistingFolder?.ids];
      return [];
    })();

    if (selectedAction === 'folderDocs') {
      callBulkActionsLikePopupApiCall(
        'FOLDER',
        newFolderId ? [newFolderId.toString()] : bulkActionTreeIDs,
        docCount ?? 0
      );
    } else if (selectedAction === 'unfolderDocs') {
      setIsBulkFolderAction(false);
      doActionApiCall(
        newFolderId ? [newFolderId.toString()] : bulkActionTreeIDs,
        selectedAction,
        docCount
      );
    }
  };

  const onActionLikeChange = (
    index: number,
    status: boolean,
    updatedActionItems: IActionListItem[]
  ) => {
    setActionLikeChangeList(updatedActionItems);
  };

  const handleActionLikeContinue = (docCount?: number) => {
    setNonBgTotalDocCount(docCount ?? 0);
    if (isBulkTagAction) {
      setIsBulkWarning(true);
      setIsActionLikeModal(false);
      setAnalyticsIndicatorStatus(0);
    } else {
      const bulkActionTreeIDs = (() => {
        if (Array.isArray(selectedNewExistingFolder?.ids))
          return selectedNewExistingFolder.ids;
        if (typeof selectedNewExistingFolder?.ids === 'string')
          return [selectedNewExistingFolder?.ids];
        return [];
      })();
      doActionApiCall(bulkActionTreeIDs, bulkSelectedAction, docCount);
    }
  };

  const handleActionLikeCancel = () => {
    setIsActionLikeModal(false);

    if (selectedNewExistingFolder?.type === 'new') {
      resetFolderActionStates();
    }
  };

  const resetFolderActionStates = () => {
    setNonBgTotalDocCount(0);
    setIsBulkWarning(false);
    setIsBulkTagAction(false);
    setMasterDocListId(0);
    setMasterDocListId(0);
    setSelectedNewExistingFolder(null);
    setBulkSelectedAction('');
    setActionLikeChangeList([]);
    setIsActionLikeModal(false);
    setIsBulkFolderAction(false);
    setAnalyticsIndicatorStatus(0);
  };

  const handleActionLikeClose = () => {
    setIsActionLikeModal(false);
    if (selectedNewExistingFolder?.type === 'new') {
      setIsBulkFolderAction(false);
      resetFolderActionStates();
    }
  };

  const handleBulkTagContinue = async (
    selectedAction: string,
    docCount?: number | undefined,
    newTagId?: number | undefined
  ) => {
    const bulkActionTreeIDs = (() => {
      const ids = selectedNewExistingTag?.ids;
      if (Array.isArray(ids)) return ids;
      if (typeof ids === 'string') return [ids];
      return [];
    })();
    setNewTagId(newTagId);

    if (selectedAction === 'untagDocs') {
      setIsBulkWarning(true);

      return;
    }

    if (selectedAction === 'tagDocs') {
      callBulkActionsLikePopupApiCall(
        'TAG',
        newTagId ? [newTagId.toString()] : bulkActionTreeIDs,
        docCount ?? 0
      );
    }
  };

  const getSelectedNewExistingTag = (): string[] => {
    const ids = selectedNewExistingTag?.ids;
    if (!isEmpty(ids)) {
      if (typeof ids === 'string') return ids ? [ids] : [];
      return ids || [];
    }
    return [];
  };

  const handleBulkWarning = () => {
    setActionLikeChangeList(undefined);

    const assignmentId = selectedAssignment?.assignmentId ?? 0;

    const bulkActionTreeIDs: string[] = (() => {
      const ids = selectedNewExistingTag?.ids;
      if (Array.isArray(ids)) return ids;
      if (typeof ids === 'string') return [ids];
      return [];
    })();

    const {includeNearDuplicates, includeFamilyMember, includeEmailThreads} =
      getIncludeFlags(actionLikeChangeList);

    const doActionNonBgPayload = {
      sourceType: SourceTypeCode.ASSIGNMENT,
      actionType: 'TAG',
      elementId: 'divBulkAction',
      selectedList: {
        contextContainerID: assignmentId,
        isInverse: false,
        isAllChildrenSelected: true,
        lstBulkActionContext: [
          {
            id: assignmentId,
            includePureHits: true,
            includeFamily: includeFamilyMember,
            includeEmailThread: includeEmailThreads,
            includeNearDupe: includeNearDuplicates,
            includeConcept: false,
            includeUniquePureHits: true,
            includeUniqueFamilyMembers: true,
          },
        ],
        excludedLstBulkActionContext: [],
        sourceID: assignmentId,
        filters: [],
        sqlUnitParam: [],
        stR_SelectedSearchIds: [],
      },
      destinationList: {
        isAdd: true,
        isQuick: true,
        bulkActionTreeIDs: newTagId ? [newTagId.toString()] : bulkActionTreeIDs,
        sampleMethod: 'STANDARD',
        numberToAssign: nonBgTotalDocCount,
        includeFamily: includeFamilyMember,
        includeEmailThread: includeEmailThreads,
        includeNearDupe: includeNearDuplicates,
      },
      documentListMasterID: 0,
      strSelections: [],
    };
    doActionNonBg(doActionNonBgPayload);
  };

  return (
    <Box className="assignment-filter-data-grid">
      {toast.open && (
        <ArrToastNotification
          message={toast.message}
          onClose={() => setToast({...toast, open: false})}
          status={toast.type}
        />
      )}
      <Box className="assignment-filter">
        <ArrButton
          variant="primary"
          size="small"
          dataTestId="action-button"
          endIcon={isMenuOpen ? <ArrowDropUp /> : <ArrowDropDownIcon />}
          aria-label={t('assignment.dataGrid.action')}
          className={`action-dropdown-button action-dropdown-button--selected`}
          onClick={(e) => {
            actionMenuRef.current?.open(e.currentTarget);
            setIsMenuOpen(true);
          }}
          id="action-button"
          name="action-button"
        >
          {t('assignment.dataGrid.action')}
        </ArrButton>
        <ArrMenu
          ref={actionMenuRef}
          items={actionMenuItems}
          direction="bottom-left"
          subMenuDirection="right"
          onItemClick={(e) => handleActionMenuItemClick(e)}
          className="action-submenu"
          onClose={() => {
            setIsMenuOpen(false);
          }}
          id="action-submenu"
        />
        <ArrIconButton
          icon={<AddFieldSortIcon className="add-field-sort-icon" />}
          onClick={handleManageColumnsClick}
          ariaLabel={t('assignment.dataGrid.showHideColumns')}
          shape="default"
          size="medium"
          className="add-filed-sort-icon-btn"
          testId="assignments-data-grid-show-hide-columns-icon-button"
        />
        <ArrMenu
          id="manage-columns-menu"
          ref={menuRef2}
          items={items}
          direction="bottom-right"
          className="assignments-columns"
          keepOpenOnItemClick={true}
          renderItem={(item) => (
            <ColumnMenuItem
              id={String(item.id)}
              label={item.label}
              checked={Boolean(item.checked)}
              disabled={Boolean(item.disabled)}
              onToggle={() => item.onToggle?.()}
            />
          )}
        />
      </Box>
      <Box
        className={`grid-main ${isAssignmentListLoading ? 'grid-loading' : ''}`}
      >
        <ArrDataGrid
          scrollbarSize={0}
          rowHeight={ROW_HEIGHT}
          rows={rows}
          columns={updatedColumns}
          currentPage={pageNumber}
          itemsPerPage={itemsPerPage}
          pageSize={itemsPerPage}
          entityType={'entries'}
          primaryKey="assignmentId"
          onSortModelChange={handleSortModelChange}
          sortModel={sortModel}
          className="assignment-grid"
          noRowsOverlay={NoRowsOverlay}
          columnVisibilityModel={visibility}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          loading={
            isAssignmentListLoading || isDocViewLoading || isDocListLoading
          }
          showPerPageOptions={true}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          rowCount={assignmentList?.totalRecords}
          onPageChange={onPageChangeHandler}
          setItemsPerPage={setItemsPerPage}
          disableVirtualization={true}
          getRowClassName={(params) =>
            params.row.assignmentId === selectedRowId ? 'selected-row' : ''
          }
        />
      </Box>
      {showModal && (
        <ArrModal
          showModal={showModal}
          onClose={handleSecondaryButton}
          title={
            selectedActionMenu === 'copy_Assignment'
              ? t('assignment.copy.modalTitle')
              : t('assignment.delete.areYouSure')
          }
          description={modalDescription}
          showClose={false}
          dataTestId="assignments-datagrid-modal"
          buttons={[
            {
              id: '1',
              label: t('common.no'),
              onClick: handleSecondaryButton,
              variant: 'secondary',
              dataTestId: 'adm-no-btn',
            },
            {
              id: '2',
              label: t('common.yes'),
              onClick: handlePrimaryButton,
              variant: 'primary',
              loading: checkLoading(),
              dataTestId: 'adm-yes-btn',
            },
          ]}
        />
      )}
      <ArrDocMaxLimitWarning
        totalDocumentCount={selectedAssignment?.totalDocuments ?? 0}
        show={showDocLimitDialog}
        destination={docLimitSourceTypeCode}
        onContinue={onContinueDocLimit}
        onCancel={onCancelDocLimit}
      />
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
      {isBulkFolderAction && (
        <BulkFolderUnFolder
          data-testid="bulk-folder"
          isOpen={isBulkFolderAction}
          setOpenActionMenu={setIsBulkFolderAction}
          handleBulkFolderContinue={handleBulkFolderContinue}
          masterDocListId={masterDocListId}
          setSelectedNewExistingFolder={setSelectedNewExistingFolder}
          isNonbg={true}
          nonBgStandardCount={nonBgStandardCount}
        />
      )}
      {isBulkTagAction && (
        <BulkTagUntag
          isOpen={isBulkTagAction}
          setOpenActionMenu={setIsBulkTagAction}
          handleBulkTagContinue={handleBulkTagContinue}
          masterDocListId={0}
          setSelectedNewExistingTag={setSelectedNewExistingTag}
          isNonbg={true}
          nonBgStandardCount={nonBgStandardCount}
        />
      )}
      {/* Loading Spinner for Folder Docs Actions */}
      {(isLoadingBulkActionsPopup ||
        isLoadingBulkActionsLikePopup ||
        isDoActionLoading) && <ArrLoadingSpinner overlay isOpen />}
      {/* Action Like Documents Modal */}
      {isActionLikeModal && (
        <ActionLikeDocuments
          dataTestId="adg-action-like-documents"
          title={t('docExplorer.actionMenu.actionLikeDocuments.title')}
          showModal={isActionLikeModal}
          onClose={handleActionLikeClose}
          onCancel={handleActionLikeCancel}
          onContinue={handleActionLikeContinue}
          actionList={initialActionList}
          onActionChange={onActionLikeChange}
          continueButtonLabel={t(
            `docExplorer.actionMenu.actionLikeDocuments.finalize.${isBulkFolderAction ? 'bulkFolder' : 'bulkTag'}`
          )}
          masterDocListId={masterDocListId}
          actionType={isBulkFolderAction ? 'FOLDER' : 'TAG'}
          analyticsIndicatorStatus={analyticsIndicatorStatus}
          isNonBg={true}
          nonBgAssignmentId={selectedAssignment?.assignmentId ?? 0}
          nonBgDocCount={nonBgStandardCount}
          sourceTypeCode={SourceTypeCode.ASSIGNMENT}
        />
      )}
      {isBulkWarning && (
        <BulkTaggingWarning
          onClose={() => {
            setIsBulkWarning(false);
            setActionLikeChangeList(undefined);
            if (selectedNewExistingTag?.type === 'new') {
              setIsBulkTagAction(false);
            }
          }}
          showModal={isBulkWarning}
          sourceType={SourceTypeCode.ASSIGNMENT}
          actionLikeChangeList={actionLikeChangeList}
          masterDocListId={0}
          selectedNewExistingTag={getSelectedNewExistingTag()}
          dataTestId="adg-bulk-tag-warning"
          buttons={[
            {
              id: '1',
              label: t('common.cancel'),
              onClick: () => {
                setIsBulkWarning(false);
                setActionLikeChangeList(undefined);
                if (selectedNewExistingTag?.type === 'new') {
                  setIsBulkTagAction(false);
                }
              },
              variant: 'secondary',
              dataTestId: 'ibw-cancel-btn',
            },
            {
              id: '2',
              label: t('common.ok'),
              onClick: handleBulkWarning,
              variant: 'primary',
              dataTestId: 'ibw-ok-btn',
            },
          ]}
        />
      )}
    </Box>
  );
};

export default AssignmentsDataGrid;
